/**
 * Device fingerprinting — vanilla JS, no external deps.
 * Generates a stable SHA-256 hex of:
 *  - canvas render
 *  - WebGL renderer
 *  - user agent + platform + cpu cores + screen
 *  - timezone + language
 *  - audio context
 *
 * Note: this is NOT cryptographically unforgeable (advanced users with
 * spoofing extensions can bypass it). For an additional layer the server
 * also checks IP rate-limits, but the combined effort prevents casual
 * multi-account abuse.
 */

function canvasFP() {
  try {
    const c = document.createElement("canvas");
    c.width = 280;
    c.height = 60;
    const ctx = c.getContext("2d");
    if (!ctx) return "no-canvas";
    ctx.textBaseline = "top";
    ctx.font = "16px 'Segoe UI', Arial, sans-serif";
    ctx.fillStyle = "#0ff";
    ctx.fillRect(0, 0, 280, 60);
    ctx.fillStyle = "#069";
    ctx.fillText("⚡IARisk·fp 2026 åéîøü", 4, 4);
    ctx.strokeStyle = "rgba(102,204,0,0.7)";
    for (let i = 0; i < 10; i++) {
      ctx.beginPath();
      ctx.arc(140 + i * 4, 30, 18, 0, Math.PI * 2, true);
      ctx.stroke();
    }
    return c.toDataURL();
  } catch {
    return "canvas-error";
  }
}

function webglFP() {
  try {
    const c = document.createElement("canvas");
    const gl = c.getContext("webgl") || c.getContext("experimental-webgl");
    if (!gl) return "no-webgl";
    const ext = gl.getExtension("WEBGL_debug_renderer_info");
    if (!ext) return "no-debug-ext";
    return [
      gl.getParameter(ext.UNMASKED_VENDOR_WEBGL) || "",
      gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) || "",
      gl.getParameter(gl.VERSION) || "",
      gl.getParameter(gl.SHADING_LANGUAGE_VERSION) || "",
    ].join("|");
  } catch {
    return "webgl-error";
  }
}

function audioFP() {
  try {
    if (!window.OfflineAudioContext) return "no-audio";
    const ctx = new OfflineAudioContext(1, 44100, 44100);
    const osc = ctx.createOscillator();
    const compressor = ctx.createDynamicsCompressor();
    osc.type = "triangle";
    osc.frequency.value = 10000;
    osc.connect(compressor);
    compressor.connect(ctx.destination);
    osc.start(0);
    // We don't actually render audio (async) — return config snapshot
    return `${ctx.sampleRate}|${compressor.threshold.value}|${compressor.knee.value}|${compressor.ratio.value}`;
  } catch {
    return "audio-error";
  }
}

async function sha256Hex(input) {
  const enc = new TextEncoder().encode(input);
  if (window.crypto && window.crypto.subtle) {
    const buf = await window.crypto.subtle.digest("SHA-256", enc);
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }
  // Fallback (super weak — only for ancient browsers)
  let h = 5381;
  for (let i = 0; i < input.length; i++) {
    h = ((h << 5) + h) ^ input.charCodeAt(i);
  }
  return Math.abs(h).toString(16).padStart(16, "0").repeat(4);
}

const STORAGE_KEY = "iarisk_device_fp_v1";

export async function getDeviceFingerprint() {
  // Cache: same browser session returns same fp
  const cached = localStorage.getItem(STORAGE_KEY);
  if (cached && cached.length >= 32) return cached;

  const parts = [
    navigator.userAgent || "",
    navigator.platform || "",
    navigator.language || "",
    String(navigator.hardwareConcurrency || 0),
    String(navigator.deviceMemory || 0),
    `${screen.width}x${screen.height}x${screen.colorDepth}`,
    new Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    new Date().getTimezoneOffset(),
    Object.keys(navigator.plugins || {}).length,
    canvasFP(),
    webglFP(),
    audioFP(),
  ];
  const composite = parts.join("§");
  const fp = await sha256Hex(composite);
  try {
    localStorage.setItem(STORAGE_KEY, fp);
  } catch (storageErr) {
    // Private-mode / quota-full / disabled — log so it's visible in DevTools but never throw
    console.debug("[deviceFingerprint] cache write failed:", storageErr?.message);
  }
  return fp;
}
